import React from 'react';
import { Container, Paper, Typography, Button, Box, CircularProgress } from '@mui/material';
import { CheckCircle, ArrowForward } from '@mui/icons-material';
import { useApp } from '../context/AppContext';
import { colors, radii } from '../theme/colors';

/**
 * Post-Stripe-checkout landing page. Rendered when `currentPage` is
 * 'subscription-confirmed' (see AppContext for how this is set after the
 * `session_id` query param arrives or the user hits `/subscription-confirmed`).
 *
 * Two visual states, both driven by AppContext:
 * - Verifying: when `planType` is still 'free' (Stripe webhook hasn't applied
 *   the new plan yet). Shows a spinner. If `isVerifyingLong` is true the user
 *   gets a "Refresh" escape hatch.
 * - Confirmed: as soon as `planType` is anything other than 'free'. Shows a
 *   success card and a button back to the converter.
 *
 * Takes no props — all state comes from `useApp()`.
 */
export default function SubscriptionConfirmed() {
  const { planType, isVerifyingLong, setCurrentPage, fetchSubscriptionStatus } = useApp();

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper elevation={0} sx={{
        p: 5, textAlign: 'center', borderRadius: `${radii.card}px`,
        bgcolor: colors.surface, border: '1px solid', borderColor: colors.borderSubtle
      }}>
        {(!planType || planType === 'free') ? (
          <>
            <Box sx={{ width: 96, height: 96, borderRadius: '50%', bgcolor: colors.powderTint, border: `1px solid ${colors.borderSubtle}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
              <CircularProgress size={48} sx={{ color: colors.teal700 }} />
            </Box>
            <Typography variant="h4" component="h1" gutterBottom sx={{ color: colors.textPrimary, fontWeight: 700 }}>
              Verifying Subscription...
            </Typography>
            <Typography variant="body1" sx={{ color: colors.textSecondary, mb: 4, fontSize: '1.1rem' }}>
              Please wait while we confirm your payment with Stripe. This takes just a moment.
            </Typography>
            {isVerifyingLong && (
              <>
                <Button variant="outlined" color="warning"
                  onClick={() => { fetchSubscriptionStatus(); window.location.reload(); }}
                  sx={{ mt: -2, mb: 3 }}>
                  Taking too long? Click to Refresh
                </Button>
                <Typography variant="caption" sx={{ display: 'block', color: colors.textSecondary, maxWidth: 400, mx: 'auto' }}>
                  If you have completed payment, you can safely proceed. Your plan will activate shortly.
                </Typography>
              </>
            )}
          </>
        ) : (
          <>
            <Box sx={{ width: 96, height: 96, borderRadius: '50%', bgcolor: colors.success100,
              display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
              <CheckCircle sx={{ fontSize: 56, color: colors.success500 }} />
            </Box>
            <Typography variant="h4" component="h1" gutterBottom sx={{ color: colors.textPrimary, fontWeight: 700 }}>
              Subscription Confirmed!
            </Typography>
            <Typography variant="body1" sx={{ color: colors.textSecondary, mb: 4, fontSize: '1.1rem' }}>
              Your plan is now active. Thank you for subscribing to EML/MSG Converter.
              You now have access to premium features and limits.
            </Typography>
          </>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Button variant="contained" size="large" fullWidth
            onClick={() => setCurrentPage('converter')} startIcon={<ArrowForward />}
            sx={{ py: 1.5, fontSize: '1rem' }}>
            Go to Dashboard
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}
